/**
 * Screenshot capture for a bug report.
 *
 * Uses `getDisplayMedia`, not DOM rasterisation. The Back Office made the same choice and recorded
 * why: html2canvas and friends redraw the DOM themselves and misrender exactly what this app is
 * made of, virtualised lists, SVG charts and a Google Map canvas. A screenshot that does not show
 * the map is worthless on a report about the map. It also adds no dependency.
 *
 * The cost is honest: the browser asks the user to choose a window or tab. There is no way to take
 * a real screen capture on the web without that prompt, and it is the price of an image that shows
 * what the user is actually looking at.
 */

/**
 * 5 MB, matching the Back Office.
 *
 * The old 3.5 MB figure existed only because the image travelled through API Gateway and Lambda as
 * base64, which inflates binary by about a third and had to fit under Lambda's 6 MB synchronous
 * event cap. The upload now goes straight from the browser to S3, so nothing in the middle carries
 * the bytes and that headroom is no longer needed.
 */
export const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024;

const MAX_WIDTH = 2560;
const MAX_HEIGHT = 1440;
const JPEG_QUALITIES = [0.85, 0.7, 0.55, 0.4];

export class CaptureCancelledError extends Error {
  constructor() {
    super('The user dismissed the screen picker.');
    this.name = 'CaptureCancelledError';
  }
}

export class CaptureUnsupportedError extends Error {
  constructor() {
    super('This browser cannot capture the screen.');
    this.name = 'CaptureUnsupportedError';
  }
}

/**
 * Hides every CDK overlay, returning a function that puts them back.
 *
 * Without this the capture contains the report dialog sitting over the page the user is trying to
 * report, and the dialog's backdrop tints the whole image grey. Both make the screenshot close to
 * useless, which is the opposite of the point.
 *
 * `visibility: hidden` rather than `display: none` on purpose: the overlay is removed from view
 * without being removed from layout, so nothing behind it reflows. `display: none` would let the
 * page shift underneath and the user would capture a subtly different layout from the one they
 * were complaining about.
 *
 * It hides the container rather than the dialog panel because the backdrop is a sibling of the
 * panel, not a child, so hiding the panel alone leaves the grey wash behind.
 */
export function hideOverlays(): () => void {
  const containers = Array.from(document.querySelectorAll<HTMLElement>('.cdk-overlay-container'));
  const previous = containers.map((c) => c.style.visibility);
  containers.forEach((c) => {
    c.style.visibility = 'hidden';
  });
  // Restoring the previous value, not clearing it, so this cannot quietly override a visibility
  // the application set for its own reasons.
  return () => {
    containers.forEach((c, i) => {
      c.style.visibility = previous[i] ?? '';
    });
  };
}

/**
 * Prompts for a surface and returns one still frame.
 *
 * The track is stopped in a `finally`: leaving it running leaves the browser's "sharing your
 * screen" indicator up for the rest of the session, which reads as the app still watching.
 */
export async function captureScreen(): Promise<HTMLCanvasElement> {
  if (!navigator.mediaDevices?.getDisplayMedia) throw new CaptureUnsupportedError();

  let stream: MediaStream | undefined;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
  } catch (error) {
    // Every browser reports "the user clicked cancel" as a rejection, and cancelling is a normal
    // thing to do, not a failure to report to the user.
    throw isAbort(error) ? new CaptureCancelledError() : error;
  }

  try {
    return await frameFrom(stream);
  } finally {
    stream.getTracks().forEach((track) => track.stop());
  }
}

async function frameFrom(stream: MediaStream): Promise<HTMLCanvasElement> {
  const video = document.createElement('video');
  video.srcObject = stream;
  video.muted = true;
  await video.play();
  // One rAF after play(): the first frame is not necessarily painted when play() resolves, and
  // drawing too early yields a blank or black image.
  await new Promise((resolve) => requestAnimationFrame(resolve));

  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const context = canvas.getContext('2d');
  if (!context) throw new CaptureUnsupportedError();
  context.drawImage(video, 0, 0);

  video.pause();
  video.srcObject = null;
  return canvas;
}

/** Reads an image the user picked instead of capturing, so a phone screenshot can be attached. */
export async function canvasFromFile(file: File): Promise<HTMLCanvasElement> {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const context = canvas.getContext('2d');
  if (!context) throw new CaptureUnsupportedError();
  context.drawImage(bitmap, 0, 0);
  bitmap.close();
  return canvas;
}

/**
 * Encodes under the server's cap, degrading in the order that costs the least evidence.
 *
 * PNG first because text stays sharp, then JPEG at falling quality, then half the dimensions and
 * round again. Shrinking last: a soft screenshot of the whole screen still shows what went wrong,
 * where a crisp quarter of it may not include the thing being reported.
 */
export async function encodeUnderLimit(
  source: HTMLCanvasElement,
  limit = MAX_SCREENSHOT_BYTES,
): Promise<Blob> {
  let canvas = scaleWithin(source, MAX_WIDTH, MAX_HEIGHT);

  for (let attempt = 0; attempt < 4; attempt++) {
    const png = await toBlob(canvas, 'image/png');
    if (png && png.size <= limit) return png;

    for (const quality of JPEG_QUALITIES) {
      const jpeg = await toBlob(canvas, 'image/jpeg', quality);
      if (jpeg && jpeg.size <= limit) return jpeg;
    }

    canvas = scaleWithin(canvas, Math.round(canvas.width / 2), Math.round(canvas.height / 2));
  }

  // Four halvings from 2560 wide is 160 wide. If that is still over 5 MB something is very wrong,
  // and failing loudly beats uploading a placeholder the triager would puzzle over.
  throw new Error('Could not encode the screenshot under the size limit.');
}

function scaleWithin(source: HTMLCanvasElement, maxWidth: number, maxHeight: number): HTMLCanvasElement {
  const ratio = Math.min(maxWidth / source.width, maxHeight / source.height, 1);
  if (ratio === 1) return source;

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(source.width * ratio));
  canvas.height = Math.max(1, Math.round(source.height * ratio));
  const context = canvas.getContext('2d');
  if (!context) return source;
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'AbortError');
}

/**
 * What a reporter can draw on a capture.
 *
 * Three kinds, and the first is different in kind from the other two: a redaction REMOVES
 * information, the rest ADD it. That is why they are all flattened into the pixels together and
 * never stored as a separate overlay. An overlay travels beside the image and can be peeled off by
 * whoever receives it, so a "redaction" that is an overlay hides nothing at all. Once the pixels
 * are painted the covered content is gone from the file, which is what a reporter blacking out a
 * customer name believes is happening.
 */
export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

export type Annotation =
  | { kind: 'redact'; rect: Rect }
  | { kind: 'draw'; points: Point[] }
  | { kind: 'text'; at: Point; text: string };

/** Annotations must read on a 2560px capture and on a 600px one, so sizes scale with the image. */
export const ANNOTATION_COLOUR = '#E5343D';

function lineWidth(canvas: HTMLCanvasElement): number {
  return Math.max(2, Math.round(canvas.width / 400));
}

function fontSize(canvas: HTMLCanvasElement): number {
  return Math.max(14, Math.round(canvas.width / 55));
}

/**
 * Draws the capture plus every annotation onto a new canvas.
 *
 * One function for both the on-screen preview and the uploaded image, so what the reporter sees is
 * exactly what is sent. Drawing the preview any other way is how a redaction ends up a few pixels
 * off the thing it was meant to cover.
 *
 * The source canvas is never modified, so an annotation can be undone.
 */
export function renderAnnotated(
  source: HTMLCanvasElement,
  annotations: readonly Annotation[],
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = source.width;
  canvas.height = source.height;
  const context = canvas.getContext('2d');
  if (!context) return source;

  context.drawImage(source, 0, 0);

  for (const annotation of annotations) {
    if (annotation.kind === 'redact') {
      context.fillStyle = '#000000';
      context.fillRect(annotation.rect.x, annotation.rect.y, annotation.rect.width, annotation.rect.height);
      continue;
    }

    if (annotation.kind === 'draw') {
      if (annotation.points.length < 2) continue;
      context.strokeStyle = ANNOTATION_COLOUR;
      context.lineWidth = lineWidth(canvas);
      context.lineJoin = 'round';
      context.lineCap = 'round';
      context.beginPath();
      context.moveTo(annotation.points[0]!.x, annotation.points[0]!.y);
      for (const point of annotation.points.slice(1)) context.lineTo(point.x, point.y);
      context.stroke();
      continue;
    }

    const size = fontSize(canvas);
    context.font = `600 ${size}px sans-serif`;
    context.textBaseline = 'top';
    // A white outline under the glyphs, so red text stays readable over a red or dark screenshot.
    context.lineWidth = Math.max(2, Math.round(size / 6));
    context.strokeStyle = '#FFFFFF';
    context.strokeText(annotation.text, annotation.at.x, annotation.at.y);
    context.fillStyle = ANNOTATION_COLOUR;
    context.fillText(annotation.text, annotation.at.x, annotation.at.y);
  }

  return canvas;
}
